import React, { useState, useContext } from "react";
import { Container, Row, Col, Form, FormGroup, Button } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Login.css";
import registerImg from "../assets/images/register.png";
import userIcon from "../assets/images/user.png";
import { AuthContext } from "../context/AuthContext";
import { BASE_URL } from "../utils/config";

const Register = () => {
  const [credentials, setCredentials] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [registrationToken, setRegistrationToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!credentials.username.trim()) {
      newErrors.username = "Username is required";
    } else if (credentials.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!credentials.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(credentials.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!credentials.password) {
      newErrors.password = "Password is required";
    } else if (credentials.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setCredentials((prevCredentials) => ({
      ...prevCredentials,
      [id]: value,
    }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: "" }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    dispatch({ type: "REGISTER_START" });

    console.log("Register: Starting registration for email:", credentials.email.trim());

    // Set a 30-second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);

    try {
      const res = await fetch(`${BASE_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          username: credentials.username.trim(),
          email: credentials.email.trim(),
          password: credentials.password,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const result = await res.json();

      console.log("Register: API response received", { status: res.status, ok: res.ok });

      if (!res.ok) {
        const errorMsg = result.message || "Registration failed. Please try again.";
        console.warn("Register: Registration failed:", errorMsg);
        setError(errorMsg);
        dispatch({ type: "REGISTER_FAILURE", payload: errorMsg });
      } else {
        console.log("Register: Registration successful, OTP sent.");
        setRegistrationToken(result.registrationToken); // Store token
        setSuccess("OTP sent to your email. Please check and verify.");
        dispatch({ type: "REGISTER_SUCCESS", payload: null });
        setIsVerifying(true);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const timeoutMsg = "Server took too long to respond. This is often because the email service (SMTP) is slow or misconfigured. Please try again or check your backend logs.";
        console.error("Register: Request timed out (30s)");
        setError(timeoutMsg);
        dispatch({ type: "REGISTER_FAILURE", payload: timeoutMsg });
      } else {
        console.error("Register: Network or unexpected error:", error);
        setError("Network error. Please check your connection and try again.");
        dispatch({ type: "REGISTER_FAILURE", payload: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    console.log("Register: Verifying OTP for email:", credentials.email);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s for verification

    try {
      const res = await fetch(`${BASE_URL}/auth/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          registrationToken,
          otp,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const result = await res.json();

      console.log("Register Verify: API response received", { status: res.status, ok: res.ok });

      if (!res.ok) {
        const errorMsg = result.message || "Verification failed";
        console.warn("Register Verify: Failed:", errorMsg);
        setError(errorMsg);
      } else {
        console.log("Register Verify: Success, logging in user.");
        setSuccess("Email verified! Logging you in...");

        const userWithToken = { ...result.data, token: result.token };
        dispatch({ type: "LOGIN_SUCCESS", payload: userWithToken });

        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const timeoutMsg = "Verification request timed out. Please try again.";
        console.error("Register Verify: Timed out");
        setError(timeoutMsg);
      } else {
        console.error("Register Verify: Error:", error);
        setError("Network error.");
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <section className="login-section">
      <Container>
        <Row>
          <Col lg="8" className="m-auto">
            <div className="login__container d-flex justify-content-between">
              <div className="login__img">
                <img src={registerImg} alt="Register" />
              </div>

              <div className="login__form">
                <div className="user">
                  <img src={userIcon} alt="User" />
                </div>
                <h2>{isVerifying ? "Verify Email" : "Create Account"}</h2>
                <p className="login__subtitle">
                  {isVerifying ? "Enter the 6-digit code sent to your email" : "Sign up to get started"}
                </p>

                {error && (
                  <div className="alert alert-danger">
                    <i className="ri-error-warning-line"></i> {error}
                  </div>
                )}
                {success && (
                  <div className="alert alert-success">
                    <i className="ri-checkbox-circle-line"></i> {success}
                  </div>
                )}

                {!isVerifying ? (
                  <Form onSubmit={handleSubmit}>
                    <FormGroup>
                      <label htmlFor="username">Username</label>
                      <div className="input-wrapper">
                        <i className="ri-user-line input-icon"></i>
                        <input
                          type="text"
                          placeholder="Enter your username"
                          id="username"
                          value={credentials.username}
                          onChange={handleChange}
                          className={errors.username ? "error" : ""}
                          autoComplete="username"
                        />
                      </div>
                      {errors.username && (
                        <span className="error-message">
                          <i className="ri-error-warning-line"></i> {errors.username}
                        </span>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="email">Email Address</label>
                      <div className="input-wrapper">
                        <i className="ri-mail-line input-icon"></i>
                        <input
                          type="email"
                          placeholder="Enter your email"
                          id="email"
                          value={credentials.email}
                          onChange={handleChange}
                          className={errors.email ? "error" : ""}
                          autoComplete="email"
                        />
                      </div>
                      {errors.email && (
                        <span className="error-message">
                          <i className="ri-error-warning-line"></i> {errors.email}
                        </span>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="password">Password</label>
                      <div className="password__input">
                        <i className="ri-lock-line input-icon"></i>
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          id="password"
                          value={credentials.password}
                          onChange={handleChange}
                          className={errors.password ? "error" : ""}
                          autoComplete="new-password"
                        />
                        <i
                          className={`password-toggle ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}
                          onClick={togglePasswordVisibility}
                        ></i>
                      </div>
                      {errors.password && (
                        <span className="error-message">
                          <i className="ri-error-warning-line"></i> {errors.password}
                        </span>
                      )}
                    </FormGroup>

                    <Button
                      className="btn secondary__btn auth__btn"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner"></span> Creating Account...
                        </>
                      ) : (
                        "Create Account"
                      )}
                    </Button>
                  </Form>
                ) : (
                  <Form onSubmit={handleVerify}>
                    <FormGroup>
                      <label htmlFor="otp">Verification Code</label>
                      <div className="input-wrapper">
                        <i className="ri-key-line input-icon"></i>
                        <input
                          type="text"
                          placeholder="Enter 6-digit code"
                          id="otp"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          maxLength="6"
                          required
                        />
                      </div>
                    </FormGroup>

                    <Button
                      className="btn secondary__btn auth__btn"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner"></span> Verifying...
                        </>
                      ) : (
                        "Verify & Login"
                      )}
                    </Button>

                    <div className="text-center mt-3">
                      <Button color="link" onClick={() => setIsVerifying(false)}>Back</Button>
                    </div>
                  </Form>
                )}

                <div className="login__footer">
                  <p>
                    Already have an account?{" "}
                    <Link to="/login" className="register-link">
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Register;
